import React, { useState } from 'react';
import { useAppSelector } from '../hooks/redux';
import { Button } from './ui/Button';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; files: File[] }) => Promise<void>;
  submitting: boolean;
  error: string | null;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  submitting,
  error
}) => {
  const { user, mongoUser } = useAppSelector((state) => state.auth);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSelectedFiles([]);
    setLocalError(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      // Validate file sizes (max 10MB each)
      const maxSize = 10 * 1024 * 1024;
      const invalidFiles = files.filter(file => file.size > maxSize);

      if (invalidFiles.length > 0) {
        setLocalError(`Some files exceed the 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
        return;
      }

      // Max 5 files
      if (files.length > 5) {
        setLocalError('Maximum 5 files allowed');
        return;
      }

      setSelectedFiles(files);
      setLocalError(null);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !description.trim()) {
      setLocalError('Title and description are required');
      return;
    }

    await onSubmit({
      title,
      description,
      files: selectedFiles
    });
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop">
      <div className="max-w-lg w-full max-h-[85vh] overflow-y-auto modal-panel">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-3">
              Send Feedback
            </h2>
            <Button variant="tertiary" size="sm" onClick={handleClose} className="!p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          {(error || localError) && (
            <div className="mb-4 bg-error-tint border border-error text-ink px-4 py-3 rounded-lg text-sm">
              {error || localError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Auto-filled user info */}
            <div className="mb-4 p-3 bg-surface-subtle rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-ink-muted">Name</label>
                  <div className="mt-0.5 text-sm text-ink">{mongoUser?.name || 'N/A'}</div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink-muted">Email</label>
                  <div className="mt-0.5 text-sm text-ink">{mongoUser?.email || user?.email || 'N/A'}</div>
                </div>
              </div>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Title <span className="text-error">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-transparent text-sm"
                placeholder="Brief summary of your feedback"
                required
              />
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Description <span className="text-error">*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-ink-border rounded-lg focus:ring-2 focus:ring-signature focus:border-transparent text-sm"
                placeholder="Tell us what's on your mind..."
                required
              />
            </div>

            {/* File Upload */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-ink-secondary mb-1">
                Attachments (Optional)
              </label>
              <div className="mt-1 flex justify-center px-4 pt-4 pb-4 border-2 border-ink-border border-dashed rounded-lg">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-10 w-10 text-ink-muted"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex text-sm text-ink-secondary">
                    <label
                      htmlFor="feedback-file-upload"
                      className="relative cursor-pointer bg-surface rounded-md font-medium text-signature hover:text-signature-strong"
                    >
                      <span>Upload files</span>
                      <input
                        id="feedback-file-upload"
                        type="file"
                        className="sr-only"
                        multiple
                        onChange={handleFileChange}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-ink-muted">
                    PNG, JPG, PDF, DOC up to 10MB (max 5 files)
                  </p>
                </div>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-surface-subtle rounded-lg"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-ink-muted mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm text-ink-secondary">{file.name}</span>
                        <span className="ml-2 text-xs text-ink-muted">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={() => removeFile(index)}
                        className="!p-1 text-error hover:opacity-80"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Feedback'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
